<template>
  <delete v-if="onDelete" :dialog="onDelete" :message="'Are you sure you want to delete this template?'" :id="deleteID" :title="'Delete Template'" @onChange="deleteConfirmation" @close="closeConfirmation"/>
  <domain v-if="domainDialog" :dialog="domainDialog" @close="closeDomainDialog" />
  <div>
    <div class="mt-5 pt-4">
      <nav class="mb-5" style="--bs-breadcrumb-divider: url(&#34;data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath d='M2.5 0L1 1.5 3.5 4 1 6.5 2.5 8l4-4-4-4z' fill='black'/%3E%3C/svg%3E&#34;);" aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item" style="font-size:24px; line-height: 24px; font-weight:900; color:#435666;"><strong>Settings</strong></li>
          <li class="breadcrumb-item active" aria-current="page" style="font-size:24px; line-height: 24px; font-weight:300; color:#435666;">Phishing</li>
        </ol>
      </nav>


			<div class="mt-4">
				<div class="row">
					<div class="col-md-12">
						<div class="card shadow p-3 mb-5 bg-white rounded border-0">
							<div class="card-body">
                <div class="row align-items-center mb-4 gy-3">
                  <!-- Left: Search input & Action Pill Buttons -->
                  <div class="col-12 col-lg-8">
                    <div class="d-flex flex-wrap align-items-center gap-3">
                      <div style="font-size: 14px; position: relative; width: 331px; max-width: 100%;">
                        <div class="input-group">
                          <input type="text" class="form-control" placeholder="Search template..." aria-label="Search template" v-model="search" aria-describedby="search" style="box-sizing: border-box; width: 100%; height: 40px; border: 1px solid #D9D9D9; border-radius: 20px; padding-left: 40px;">
                          <span class="input-group-addon" style="position: absolute; left: 16px; top: 50%; transform: translateY(-50%); background-color: transparent; pointer-events: none; color: #8c98a4;">
                            <i class="fas fa-search"></i>
                          </span>
                        </div>
                      </div>
                      <div class="d-flex flex-wrap align-items-center gap-2">
                        <button type="button" class="btn btn-primary btn-create" @click="templateDialog = true;" v-tooltip.top="'Add new Template'">
                          <i class="fas fa-upload me-1"></i> Upload
                        </button>
                        <button type="button" class="btn btn-outline-primary btn-create" @click="domainDialog = true;" v-tooltip.top="'Manage Domains & DKIM Health'">
                          <i class="fas fa-sliders-h me-1"></i> Domains
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Right: Sort & View Toggle Controls -->
                  <div class="col-12 col-lg-4">
                    <div class="d-flex align-items-center justify-content-lg-end gap-3">
                      <div class="d-flex align-items-center" style="font-size: 14px;">
                        <span class="text-default me-1">
                          <i class="filter-icon fa-solid fa-sort me-1 text-muted"></i>
                          <strong>Sort by: </strong>
                        </span>
                        <div class="dropdown">
                          <span class="dropdown-toggle text-info fw-bold" id="sortBy" data-bs-toggle="dropdown" aria-expanded="false" style="cursor: pointer;">
                            {{ selectedSort }}
                          </span>
                          <ul class="dropdown-menu dropdown-menu-end shadow-sm" aria-labelledby="sortBy">
                            <li v-for="(item, index) in sortByOptions" :key="index">
                              <span class="dropdown-item" style="cursor: pointer;" @click="sortBy = item.value; selectedSort = item.label;">
                                {{ item.label }}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div class="btn-group" role="group" aria-label="View switch toggle">
                        <button type="button" class="btn btn-square btn-labeled" :class="view.list ? 'btn-info text-white' : 'btn-default text-info'" :aria-pressed="view.list" @click="view.list = true; view.grid = false;" v-tooltip.top="'Switch to List View'">
                          <span class="btn-label"><i class="fas fa-list"></i></span>
                        </button>
                        <button type="button" class="btn btn-square btn-labeled" :class="view.grid ? 'btn-info text-white' : 'btn-default text-info'" :aria-pressed="view.grid" @click="view.grid = true; view.list = false;" v-tooltip.top="'Switch to Tile View'">
                          <span class="btn-label"><i class="fas fa-border-all"></i></span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div class="mt-5">
                    <div class="row">

                      <div v-if="loading" class="text-center align-middle">
                        <img src="@/assets/img/loading.svg">
                      </div>

                      <div v-if="templates.length == 0 && !loading" class="text-center">
                        <div class="card-body align-middle text-default" style="margin: auto; width: 50%; padding: 100px;">
                          <h2>No Data</h2>
                        </div>
                      </div>
                      <div v-else>
                        <div class="col-12">
                          <h2>Phishing Templates</h2>
                        </div>
                        <!-- grid view -->
                        <div class="row">
                          <div class="col-lg-3 col-md-3 col-sm-6 col-xs-12 mb-2" v-for="(item, index) in templates" :key="index">
                            <div class="card-body text-default" v-if="!loading && view.grid" style="padding: 2px;">
                              <div style="position: relative; width: 100%; height: 0; padding-bottom: 56.25%;">
                                <img v-lazy="item.thumbnail" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; box-shadow: 0 0 5px gray;" alt="Template Thumbnail">
                              </div>
                              <div class="pa-0 mt-2">
                                <h6 style="font-size:14px;"><strong>{{ item.name}}</strong></h6>
                                <span><small class="text-muted">Vertex or Client Input: </small><strong style="font-size:14px;">{{ item.template_mapping.input_type ? "Vertex" : "Client" }}</strong></span>
                                <br />
                                <span><small class="text-muted">Client Name: </small> <strong style="font-size:14px;">{{  item.organisation_id == 0 ? "N/A" : "Client" }}</strong></span>
                                <br>
                                <div class="inline-block">
                                  <!-- edit button -->
                                  <button type="button" v-tooltip.top="'Edit Template'" class="btn btn-default btn-circle custom-spacing custom-icon-btn" @click="setTemplate(item)">
                                    <i class="fa fa-pen"></i>
                                  </button>
                                  <!-- delete button -->
                                  <button type="button" class="btn btn-default btn-circle-danger custom-icon-btn custom-spacing" ms-1 v-tooltip.top="'Delete Template'" @click="deleteTemplate(item.ID)">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M8 4H12C12 3.46957 11.7893 2.96086 11.4142 2.58579C11.0391 2.21071 10.5304 2 10 2C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4ZM6 4C6 2.93913 6.42143 1.92172 7.17157 1.17157C7.92172 0.421427 8.93913 0 10 0C11.0609 0 12.0783 0.421427 12.8284 1.17157C13.5786 1.92172 14 2.93913 14 4H19C19.2652 4 19.5196 4.10536 19.7071 4.29289C19.8946 4.48043 20 4.73478 20 5C20 5.26522 19.8946 5.51957 19.7071 5.70711C19.5196 5.89464 19.2652 6 19 6H18.118L17.232 16.34C17.1468 17.3385 16.69 18.2686 15.9519 18.9463C15.2137 19.6241 14.2481 20.0001 13.246 20H6.754C5.75191 20.0001 4.78628 19.6241 4.04815 18.9463C3.31002 18.2686 2.85318 17.3385 2.768 16.34L1.882 6H1C0.734784 6 0.48043 5.89464 0.292893 5.70711C0.105357 5.51957 0 5.26522 0 5C0 4.73478 0.105357 4.48043 0.292893 4.29289C0.48043 4.10536 0.734784 4 1 4H6ZM13 10C13 9.73478 12.8946 9.48043 12.7071 9.29289C12.5196 9.10536 12.2652 9 12 9C11.7348 9 11.4804 9.10536 11.2929 9.29289C11.1054 9.48043 11 9.73478 11 10V14C11 14.2652 11.1054 14.5196 11.2929 14.7071C11.4804 14.8946 11.7348 15 12 15C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V10ZM8 9C7.73478 9 7.48043 9.10536 7.29289 9.29289C7.10536 9.48043 7 9.73478 7 10V14C7 14.2652 7.10536 14.5196 7.29289 14.7071C7.48043 14.8946 7.73478 15 8 15C8.26522 15 8.51957 14.8946 8.70711 14.7071C8.89464 14.5196 9 14.2652 9 14V10C9 9.73478 8.89464 9.48043 8.70711 9.29289C8.51957 9.10536 8.26522 9 8 9Z" fill="#EB1C24"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>



                        <div v-if="!loading && view.list">
                          <!-- list view -->
                          <table class="table">
                            <thead>
                              <tr class="table-light text-default">
                                <th>Template Name</th>
                                <th>Vertex or Client Input</th>
                                <th>Client Name</th>
                                <th>Link to Domain</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody class="align-middle text-default" v-for="(item, index) in templates" :key="index">
                              <tr>
                                  <td> {{ item.name }}</td>
                                  <td>{{ item.template_mapping.input_type ? "Vertex" : "Client"}} </td>
                                  <td>
                                    {{  item.organisation_id == 0 ? "N/A" : "Client"}}
                                  </td>
                                  <td>{{ item.domain }}</td>
                                  <td>
                                    <button type="button" class="btn btn-default btn-circle ms-1" ms-1 v-tooltip.top="'Edit Template'" @click="setTemplate(item)"><i class="far fa-edit"></i></button>
                                    <button type="button" class="btn btn-default btn-circle-danger ms-1" ms-1 v-tooltip.top="'Delete Template'"><i class="fas fa-trash" @click="deleteTemplate(item.ID)"></i></button>
                                  </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

							</div>
						</div>
					</div>
				</div>
			</div>
    </div>
  </div>

  <!-- start phishing dialog -->
  <vue-final-modal
    v-model="templateDialog"
    classes="modal-overlay-container"
    content-class="modern-modal-content template-modal-standard"
    @before-close="closeDialog"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <h5 class="modal-title">{{ (buttonSave === 'save' || buttonSave === 'Save') ? 'Upload' : 'Edit' }} Phishing Template</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeDialog" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body custom-scroll">
      <!-- TEMPLATE NAME * -->
      <div class="setting-item">
        <label for="templateName">TEMPLATE NAME <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <input
            id="templateName"
            class="form-control"
            :class="{ 'border-danger': v$.template.name.$error }"
            type="text"
            placeholder="e.g. Security Policy Update"
            v-model="template.name"
            @blur="v$.template.name.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.name.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <!-- SELECT DOMAIN * -->
      <div class="setting-item">
        <label for="domainSelect">SELECT DOMAIN <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
          </div>
          <select
            id="domainSelect"
            class="form-control"
            :class="{ 'border-danger': v$.template.linkToDomain.$error }"
            v-model="template.linkToDomain"
            @blur="v$.template.linkToDomain.$touch()"
          >
            <option value="">Select a domain</option>
            <option v-for="(domain, index) in domains" :key="index" :value="domain.name">{{ domain.name }}</option>
          </select>
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.linkToDomain.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <!-- REDIRECT URL * -->
      <div class="setting-item">
        <label for="redirectUrlInput">REDIRECT URL <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
          </div>
          <input
            type="text"
            id="redirectUrlInput"
            class="form-control"
            :class="{ 'border-danger': v$.template.redirectUrl.$error }"
            v-model="template.redirectUrl"
            placeholder="e.g. https://portal.company-safety.com/verify"
            @blur="v$.template.redirectUrl.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.redirectUrl.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <!-- EMAIL CONTENT (.HTML) * -->
      <div class="setting-item" v-if="templateDialog">
        <label for="emailContentFile">EMAIL CONTENT (.HTML) <span class="text-danger">*</span></label>
        <div class="uploader-container-card">
          <FileUploader
            ref="emailContentFileRef"
            :id="'emailContentFile'"
            @fileListChange="handleEmailFileUpload"
            key="emailContentFile"
            :class="{ 'mb-2': v$.template.emailContent.$errors.length == 0 }"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.emailContent.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <!-- LANDING PAGE (.HTML) * -->
      <div class="setting-item" v-if="templateDialog">
        <label for="landingContentFile">LANDING PAGE (.HTML) <span class="text-danger">*</span></label>
        <div class="uploader-container-card">
          <FileUploader
            ref="landingContentFileRef"
            :id="'landingContentFile'"
            @fileListChange="handleLandingFileUpload"
            key="landingContentFile"
            :class="{ 'mb-2': v$.template.landingContent.$errors.length == 0 }"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.landingContent.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <!-- DISPLAY THUMBNAIL (.PNG, .JPG) * -->
      <div class="setting-item" v-if="templateDialog">
        <label for="thumbnail">DISPLAY THUMBNAIL (.PNG, .JPG) <span class="text-danger">*</span></label>
        <div class="uploader-container-card">
          <ImageUploader
            ref="thumbnailRef"
            :id="'thumbnail'"
            @fileListChange="handleThumbnailFileUpload"
            key="thumbnail"
            :acceptedFileType="'png,jpeg'"
            :imageFile="thumbnailImageFile"
            :class="{ 'mb-2': v$.template.thumbnail.$errors.length == 0 }"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.template.thumbnail.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="closeDialog" :disabled="saving">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary d-flex align-items-center"
        @click="saveTemplate"
        :disabled="saving"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ saving ? 'Saving...' : ((buttonSave === 'save' || buttonSave === 'Save') ? 'Upload Template' : 'Update Template') }}
      </button>
    </div>
  </vue-final-modal>
  <!--end phishing dialog -->
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { debounce } from '@/plugins/helper.js'
import { required } from '@vuelidate/validators'
import FileUploader from '@/components/FileUploader.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import Delete from '@/components/Delete.vue';
import Domain from '@/components/phishing/Domain.vue';


export default {
  name: "Phishing Template",

  components: {
      FileUploader,
      ImageUploader,
      Delete,
      Domain
   },


  setup() {
    return {
      v$: useVuelidate(),
    }
  },

  validations () {
    return {
      template: {
        name: {required},
        emailContent: {required},
        landingContent: {required},
        thumbnail: {required},
        linkToDomain: {required},
        redirectUrl: {required}
      },
    }
  },


  data() {
    return {
      loading: false,
      templateDialog: false,
      saving: false,
      search: "",
      view: {
        list: false,
        grid: true,
      },

      fileName: '',
      url: '',
      emailContentObject: null,
      thumbObject: null,
      isSelecting: false,
      landingName: '',
      landingContentObject: null,
      buttonSave: 'save',

      templates: [],
      template: {
        name: '',
        landingContent: null,
        emailContent: null,
        thumbnail: null,
        linkToDomain: '',
        redirectUrl: '',
      },

      sortBy: null,
      selectedSort: "Name",
      sortByOptions:[
        {
          label: "Name",
          value: "Name"
        },
        {
          label: "Created Date",
          value: "created_at"
        },
      ],
      thumbnailImageFile: null,
      onDelete: false,
      deleteID: 0,
      domainDialog: false,
      domains: [],
      orgID: this.$helper.setOrganisationID(),
    }
  },

  async created() {
    await this.getData();
    await this.getDomains();
  },

  methods: {
    formatToastMessage(msg, fallback = '') {
      const text = String(msg || fallback || '').trim();
      if (!text) return '';
      return /[.!?]$/.test(text) ? text : `${text}.`;
    },

    getData() {
      this.loading = true;

      let params = {
        filter: this.search,
        sort: this.sortBy ? this.sortBy : ''
      }

      this.$axios.get('/supadmin/phishing-template', {params})
      .then((response) => {
          if (response.data.success){
            this.templates = response.data.templates;
          } else {
            this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to fetch templates.'));
          }
        this.loading = false;
      })
      .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error fetching templates.'));
          this.saving = false;
        });
    },

    getDomains() {
      this.$axios.get('/supadmin/phishing-domain')
      .then((response) => {
          if (response.data.success){
            this.domains = response.data.domains;
          } else {
            this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to fetch domains.'));
          }
        this.loading = false;
      })
      .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error fetching domains.'));
          this.saving = false;
        });
    },


    setSelectedDomain() {
      // Find the domain object in the domains array that matches the selected linkToDomain
      const selectedDomainObj = this.domains.find(domain => domain.name === this.template.linkToDomain);
      if (selectedDomainObj) {
        this.template.linkToDomain = selectedDomainObj.name;
      }
    },

    setTemplate(item){
      this.id = item.ID;
      this.buttonSave = 'update';
      this.$axios.get("/supadmin/phishing-template/"+ item.ID)
      .then((response) => {
          if (response.data.success){
            this.templateDialog = true;
            this.template.name = response.data.template.name;

            this.template.emailContent = response.data.template.email_content;
            this.emailContentObject = response.data.template.email_content;

            this.template.landingContent = response.data.template.landing_page_content;
            this.landingContentObject = response.data.template.landing_page_content;

            this.thumbnailImageFile = response.data.template.thumbnail;
            this.template.thumbnail = response.data.template.thumbnail;
            this.template.linkToDomain = response.data.template.domain;
            this.template.redirectUrl = response.data.template.redirect_url;
            //find and match value of selected domain on edit
            this.setSelectedDomain();

            let tempEmailName = Buffer.from(response.data.template.email_content, 'base64').toString('utf-8');
            let tempLandingName = Buffer.from(response.data.template.landing_page_content, 'base64').toString('utf-8');
            this.fileName = tempEmailName.substring(0, 15)
            this.landingName = tempLandingName.substring(0, 15)
          } else
            this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to load template.'))
      })
      .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error loading template.'));
          this.saving = false;
        });
    },

    handleEmailFileUpload({files}) {
      this.emailContentObject = files
      this.template.emailContent = files
    },

    handleLandingFileUpload({files}) {
      this.landingContentObject = files
      this.template.landingContent = files
    },

    handleThumbnailFileUpload({files}) {
      this.thumbObject = files
      this.template.thumbnail = files

    },

    async saveTemplate(){
      //touch inputs for validation
      this.v$.template.name.$touch();
      this.v$.template.emailContent.$touch();
      this.v$.template.landingContent.$touch();
      this.v$.template.thumbnail.$touch();
      this.v$.template.linkToDomain.$touch();
      this.v$.template.redirectUrl.$touch();

      //validate inputs
      if (this.v$.template.name.$invalid || this.v$.template.emailContent.$invalid || this.v$.template.landingContent.$invalid || this.v$.template.thumbnail.$invalid || this.v$.template.linkToDomain.$invalid || this.v$.template.redirectUrl.$invalid) return

      //change state
      this.saving = true;

      //append data to payloads
      let formData = new FormData();
        formData.append('name', this.template.name);

        if (this.emailContentObject != null)
          formData.append('email_template', this.emailContentObject);

        if (this.landingContentObject != null)
          formData.append('landing_template', this.landingContentObject);

        if (this.thumbObject != null)
          formData.append('thumbnail', this.thumbObject);

        formData.append('domain', this.template.linkToDomain);
        formData.append('redirect_url', this.template.redirectUrl);

      //check state if saving or not, save or update depending on state
      if (this.buttonSave == 'save'){
        this.$axios.post('/supadmin/phishing-template', formData)
        .then((response) => {
          if (response.data.success){
            this.$toast.success(this.formatToastMessage(response.data.message, 'Template created successfully.'))
            this.getData();

            this.closeDialog();
            this.saving = false;
          }
          else{
            this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to create template.'))
            this.saving = false;
          }

        })
        .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error creating template.'));
          this.saving = false;
        });
      } else {
        this.$axios.put('/supadmin/phishing-template/' + this.id, formData)
        .then((response) => {
          if (response.data.success){
            this.getData();
            this.$toast.success(this.formatToastMessage(response.data.message, 'Template updated successfully.'))

            this.closeDialog();
            this.saving = false;
          }
          else{
            this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to update template.'))
            this.saving = false;
          }
        })
        .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error updating template.'));
          this.saving = false;
        });
      }
    },

    closeDialog(){
      this.id = "";
      this.templateDialog = false;
      this.template = {
        name: '',
        emailContent: null,
        landingContent: null,
        thumbnail: null,
        linkToDomain: '',
        redirectUrl: '',
      };
      this.buttonSave = 'save';
      this.fileName = '';
      this.emailContentObject = null;
      this.landingName = '';
      this.landingContentObject = null;
      this.thumbName = '';
      this.thumbObject = null;
      this.thumbnailImageFile = null;
      this.v$.$reset();
    },

    deleteTemplate(id) {
      this.onDelete = true;
      this.deleteID = id;
    },

    deleteConfirmation(value) {
      if (value.delete) {
        this.$axios.delete('/supadmin/phishing-template/' + value.id)
          .then((response) => {
              if (response.data.success){
                  this.$toast.success(this.formatToastMessage(response.data.message, 'Template deleted successfully.'))
                  this.getData();
              } else {
                  this.$toast.error(this.formatToastMessage(response.data.message, 'Failed to delete template.'))
              }
          })
          .catch(error => {
          const errorMessage = this.$helper.getErrorMessage(error);

          this.$toast.error(this.formatToastMessage(errorMessage, 'Error deleting template.'));
          this.saving = false;
        });
        }
      this.onDelete = false;
      this.deleteID = 0;

    },

    closeConfirmation(){
      this.onDelete = false;
      this.deleteID = 0;
    },

    async closeDomainDialog() {
      await this.getData();
      await this.getDomains();
      this.domainDialog = false;
    }
  },

  computed: {
    emailButtonText() {
      return this.fileName ? this.fileName : 'Select Email Content';
    },

  },

  watch: {
    search: debounce(function() {
      this.templates = []
      this.getData()
    }, 600),

    sortBy(){
      this.getData();
    },

    organisation(){
      if (this.$store.state.auth.accessToken == null) return
      this.orgID = this.$store.state.user.organisation.id
      this.init();
    }
  }

}
</script>


<style scoped>
.form-control{
  -webkit-border-radius: 0px;
     -moz-border-radius: 10px;
          border-radius: 10px;
}
.btn-create {
  -webkit-border-radius: 20px !important;
     -moz-border-radius: 20px !important;
          border-radius: 20px !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  padding: 8px 18px !important;
  margin-bottom: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.4;
  transition: all 0.2s ease-in-out;
}

.btn-primary.btn-create {
  background-color: #00A0C1 !important;
  border: 1px solid #00A0C1 !important;
  color: #ffffff !important;
}

.btn-primary.btn-create:hover {
  background-color: #008da8 !important;
  border-color: #008da8 !important;
  color: #ffffff !important;
}

.btn-outline-primary.btn-create {
  border: 1px solid #00A0C1 !important;
  color: #00A0C1 !important;
  background-color: transparent !important;
}

.btn-outline-primary.btn-create:hover {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
  color: #ffffff !important;
}

.btn-square {
  -webkit-border-radius: 0px !important;
     -moz-border-radius: 0px !important;
          border-radius: 0px !important;
  border: 1px solid #00A0C1 !important;
  padding: 6px 12px;
}

.btn-square.btn-info {
  background-color: #00A0C1 !important;
  border-color: #00A0C1 !important;
  color: #ffffff !important;
}

.btn-square.btn-default {
  background-color: #ffffff !important;
  border-color: #00A0C1 !important;
  color: #00A0C1 !important;
}

.btn-square.btn-default:hover {
  background-color: #f0f9ff !important;
  color: #008da8 !important;
}

::-webkit-input-placeholder {
   font-size: 14px!important;
}

:-moz-placeholder { /* Firefox 18- */
      font-size: 14px!important;
}
::-moz-placeholder {  /* Firefox 19+ */
      font-size: 14px!important;
}

.video {
  width:100%;
}

/* buttons override */
.btn-circle-danger {
	width: 30px;
	height: 30px;
	padding: 6px 0px;
	border-radius: 15px;
	text-align: center;
	font-size: 12px;
	line-height: 1.42857;
	border: 1px solid #EB1C24;
	color: #EB1C24;
}

 .btn-circle-danger:hover {
	border: 1px solid #EB1C24;
	color: #EB1C24;
}

.btn-circle-danger:focus{
	border: 1px solid #EB1C24;
	color: #EB1C24;
}

.text-default{
	color: #435666;
}

div.inline { float:left;}

.form-check-input:checked {
  background-color: #00a0c1;
  border-color: #00d3fd;
}

td {
  white-space: normal !important;
  word-wrap: break-word;
}

table {
  table-layout: fixed;
}


.form-control::placeholder {
    color: #555555;
  }

@media screen and (max-width: 900px) {

:deep(.modal-content) {
  width: 95%;
}
}

/* custom icons
/* Common styles for icons */
.custom-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0; /* Remove padding to avoid extra spacing */
  border-radius: 50%; /* Make the button round */
}

.custom-icon-btn svg {
  width: 20px;
  height: 20px;
}

/* Adjust the size of the edit icon */
.custom-icon-btn i.fa-pen {
  font-size: 14px;
}

/* The container holding all buttons */
.inline-block {
  display: flex;
  align-items: center; /* Vertically center the items */
  flex-wrap: wrap; /* Allow icons to wrap to the next line */
  gap: 5px; /* Add some spacing between the stacked icons */
}

/* Custom slider styles */
.custom-slider {
  position: relative;
  width: 42px;
  height: 22px;
  margin-right: 5px; /* Add some space between the slider and other icons */
}

.custom-slider .slider {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 40px; /* Adjust the width of the slider background */
  height: 20px; /* Adjust the height of the slider background */
  background-color: #B5DAC6;
  border-radius: 11px;
  cursor: pointer;
  transition: 0.4s;
}

.custom-slider .slider-button {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 20px; /* Adjust the width of the slider button */
  height: 20px; /* Adjust the height of the slider button */
  background-color: #00B972; /* Slightly different shade of green */
  border-radius: 50%;
  transition: 0.4s;
}

/* Move the slider button when it's checked (toggled on) */
.form-check-input:checked + .slider .slider-button {
  transform: translateX(20px);
}

/* Adjust the background color when the slider is toggled off (unchecked) */
.form-check-input:not(:checked) + .slider {
  background-color: #DADADA; /* A slightly grayer color */
}

/* Hide the default checkbox input */
.form-check-input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Media Query for smaller screen sizes */
@media screen and (max-width: 768px) {
  .inline-block {
    flex-wrap: wrap; /* Allow icons to wrap to the next line */
  }

  .custom-icon-btn i.fa-pen {
    font-size: 16px; /* Adjust the size as needed */
  }

  /* Adjust the size of the slider button for smaller screens */
  .custom-slider .slider-button {
    width: 18px;
    height: 18px;
  }

  /* Adjust the size of other icons for smaller screens */
  .custom-icon-btn svg {
    width: 14px;
    height: 14px;
  }
}

/* Modern Modal Standards */
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
  max-height: 90vh;
}

:deep(.template-modal-standard) {
  max-width: 580px !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

.modal-body.custom-scroll {
  padding: 1.25rem 1.5rem;
  flex-grow: 1;
  overflow-y: auto;
}

.setting-item {
  margin-bottom: 1.25rem;
}

.setting-item label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.modern-modal-content .form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
  appearance: auto;
}

.modern-modal-content .form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.uploader-container-card {
  border: 1.5px dashed #cbd5e1;
  border-radius: 8px;
  background-color: #f8fafc;
  padding: 0.5rem;
  transition: all 0.2s ease;
}

.uploader-container-card:hover {
  border-color: #00a0c1;
  background-color: #f0f9ff;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem;
  gap: 0.75rem;
  background-color: #f8fafc;
}

.modal-footer .btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.modal-footer .btn-primary {
  background-color: #00a0c1;
  color: white;
  border-color: #00a0c1;
}

.modal-footer .btn-primary:hover:not(:disabled) {
  background-color: #008da8;
  border-color: #008da8;
}

.modal-footer .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer .btn-secondary {
  background-color: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.modal-footer .btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}
</style>